import { createFileRoute, isNotFound } from '@tanstack/react-router';
import { usePortalMetadataQuery } from '@/lib/data-hooks';
import { ensureSeoData } from '@/lib/data';
import { trpc } from '@/lib/trpc';
import { Footer } from './-components/footer';
import { Header } from './-components/header';
import { Suspense } from 'react';
import { Seo } from '../-components/seo';
import { generateImageSrc, getLogoImageSize } from '@/lib/shared';
import { preloadImageOnClient, heroImageSize } from '@worldcoo/ui/lib/utils';
import { useSuspenseQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/$portalId/terms')({
	loader: async ({ context }) => {
		context.queryClient
			.ensureQueryData(trpc.portal.getAuthor.queryOptions({ locale: context.locale, portalId: context.portalId }))
			.then((data) => {
				if ('notFound' in data) return;
				if (!data.logo?.location) return;
				preloadImageOnClient(
					generateImageSrc(data.logo.location, {
						height: getLogoImageSize(context.portalId).height,
						width: (data.logo.width / data.logo.height) * getLogoImageSize(context.portalId).height,
					}),
				);
			});

		context.queryClient.ensureQueryData(
			trpc.core.getLegalContent.queryOptions({ type: 'legal-disclaimer', locale: context.locale }),
		);

		await ensureSeoData(
			context,
			context.queryClient
				.ensureQueryData(trpc.portal.getMetadata.queryOptions({ locale: context.locale, portalId: context.portalId }))
				.then((data) => {
					if ('notFound' in data) return;
					if (!data.media?.location) return;
					preloadImageOnClient(generateImageSrc(data.media.location, heroImageSize));
				}),
		);
	},
	component: Component,
});

function Component() {
	const context = Route.useRouteContext();
	const { data: html } = useSuspenseQuery(
		trpc.core.getLegalContent.queryOptions({ type: 'legal-disclaimer', locale: context.locale }),
	);

	return (
		<>
			<Suspense>
				<SuspensedHead />
			</Suspense>
			<main className="w-full">
				<Header />
				<Suspense>
					<TermsContent html={html} />
				</Suspense>
				<Footer />
			</main>
		</>
	);
}

function TermsContent({ html }: { html: string }) {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;

	return <article className="unreset-tailwind mx-auto max-w-4xl p-8" dangerouslySetInnerHTML={{ __html: html }} />;
}

function SuspensedHead() {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;

	return (
		<Seo
			title={metadata.texts.title}
			description={metadata.texts.presentation}
			image={metadata.media?.location ? generateImageSrc(metadata.media.location) : undefined}
		/>
	);
}
